import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PatientDetail } from '../features/patients/components/PatientDetail';
import { mapPatientFromApi, PatientRow } from '../features/patients/domain';
import { useApiClient } from '../lib/apiClient';
import { useI18n } from '../i18n/LanguageContext';

export default function PatientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState<PatientRow | null>(null);
  const [appointments, setAppointments] = useState<any[]>([]);
  const [permissionCodes, setPermissionCodes] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { fetchApi, fetchJson } = useApiClient();
  const { tr } = useI18n();

  useEffect(() => {
    if (!id) return;
    const controller = new AbortController();
    const apptController = new AbortController();
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [meRes, res] = await Promise.all([
          fetchJson<{ permissionCodes?: string[] }>('/users/me').catch(() => ({ permissionCodes: [] })),
          fetchApi(`/patients/${id}`, { signal: controller.signal }),
        ]);
        if (!res.ok) throw new Error(`Load failed ${res.status}`);
        const p = await res.json();
        setPermissionCodes(Array.isArray(meRes.permissionCodes) ? meRes.permissionCodes : []);
        setPatient(mapPatientFromApi(p));

        // fetch recent and upcoming appointments for this patient (last 6 months forward)
        const since = new Date();
        since.setMonth(since.getMonth() - 6);
        const startParam = encodeURIComponent(since.toISOString());
        const primaryPath = `/appointments/patient/${id}?start=${startParam}`;
        const fallbackPath = `/appointments?start=${startParam}&patientId=${id}`;

        let apptRes = await fetchApi(primaryPath, { signal: apptController.signal });
        if (!apptRes.ok && apptRes.status === 404) {
          apptRes = await fetchApi(fallbackPath, { signal: apptController.signal });
        }

        if (apptRes.ok) {
          const all = await apptRes.json();
          const filtered = all.map((a: any) => ({ ...a, type: a.type ?? a.appointmentType?.name }));
          setAppointments(filtered);
          const now = Date.now();
          const upcoming = filtered
            .filter((a: any) => new Date(a.startTime).getTime() > now)
            .sort((a: any, b: any) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
          const past = filtered
            .filter((a: any) => new Date(a.startTime).getTime() <= now)
            .sort((a: any, b: any) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());
          const next = upcoming[0];
          const last = past.find((a: any) => a.status === 'COMPLETED') ?? past[0];
          if (next || last) {
            setPatient((prev) =>
              prev
                ? {
                    ...prev,
                    upcoming: next
                      ? new Date(next.startTime).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })
                      : prev.upcoming,
                    lastVisit: last ? last.startTime : prev.lastVisit,
                  }
                : prev,
            );
          }
        } else {
          setAppointments([]);
        }
      } catch (err: any) {
        if (err.name === 'AbortError') return;
        setError(err.message || tr('Failed to load patient'));
      } finally {
        setLoading(false);
      }
    }
    load();
    return () => {
      controller.abort();
      apptController.abort();
    };
  }, [id, fetchApi, tr]);

  if (loading) {
    return <div className="card p-6 text-sm text-slate-500">{tr('Loading patient...')}</div>;
  }

  if (error || !patient) {
    return (
      <div className="card p-6 space-y-3">
        <p className="text-lg font-semibold text-ink">{error || tr('Patient not found')}</p>
        <button className="px-3 py-2 rounded-lg border border-slate-200" onClick={() => navigate('/patients')}>
          {tr('Back to patients')}
        </button>
      </div>
    );
  }

  return (
    <div className="card p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-ink">{tr('Patient Details')}</h1>
        <button className="px-3 py-2 rounded-lg border border-slate-200" onClick={() => navigate('/patients')}>
          {tr('Back to list')}
        </button>
      </div>
    <PatientDetail
        patient={patient}
        appointments={appointments}
        permissionCodes={permissionCodes}
        onPatientUpdated={(next) => setPatient(next)}
      />
    </div>
  );
}
